import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { TOOL_FORMS, resolveSections } from "@/data/toolForms";
import { ToolFormField } from "@/components/tools/ToolFormField";
import { MatterClientPicker } from "@/components/tools/MatterClientPicker";
import { MoraInterestPreview } from "@/components/tools/MoraInterestPreview";
import { fireN8nWebhook } from "@/lib/webhooks";
import { useQuery } from "@tanstack/react-query";
import type { ToolKey } from "@/data/types";

/** Tools whose drafts are persisted to the documents table. */
type PersistedTool = "affidavit" | "lod" | "resolution" | "agreement" | "compliance";
const PERSISTED_TOOLS: PersistedTool[] = ["affidavit", "lod", "resolution", "agreement", "compliance"];
const isPersistedTool = (t: ToolKey): t is PersistedTool =>
  (PERSISTED_TOOLS as ToolKey[]).includes(t);

type Props = {
  toolKey: ToolKey | null;
  onClose: () => void;
  /** Optional pre-selected matter (e.g. when launched from a matter page) */
  defaultMatterId?: string | null;
  defaultClientId?: string | null;
};

export const ToolModal = ({ toolKey, onClose, defaultMatterId = null, defaultClientId = null }: Props) => {
  const config = toolKey ? TOOL_FORMS[toolKey] : null;
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [matterId, setMatterId] = useState<string | null>(defaultMatterId);
  const [clientId, setClientId] = useState<string | null>(defaultClientId);

  // Pull profile to seed creditor/firm defaults on relevant tools.
  const { data: profile } = useQuery({
    queryKey: ["profile-defaults", user?.id],
    enabled: !!user && !!toolKey,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, firm_name")
        .eq("id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  // Reset form (with smart defaults) whenever the modal is opened with a different tool.
  useEffect(() => {
    if (!toolKey) return;
    const today = new Date().toISOString().slice(0, 10);
    const defaults: Record<string, string> = {};
    if (toolKey === "lod") {
      defaults.creditor_name = (profile?.firm_name as string) ?? (profile?.full_name as string) ?? "";
      defaults.due_date = today;
    }
    if (toolKey === "affidavit") {
      defaults.deponent_name = (profile?.full_name as string) ?? "";
    }
    if (toolKey === "compliance") {
      defaults.due_date = today;
    }
    setValues(defaults);
    setMatterId(defaultMatterId);
    setClientId(defaultClientId);
  }, [toolKey, defaultMatterId, defaultClientId, profile]);

  // ESC to close
  useEffect(() => {
    if (!toolKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [toolKey, onClose]);

  // Resolve sections from the live values so conditional fields appear
  // as the user changes their selections (e.g. agreement type).
  const sections = useMemo(
    () => (config ? resolveSections(config, values) : []),
    [config, values],
  );

  const requiredMissing = useMemo(() => {
    if (!config) return false;
    return sections.some((s) =>
      s.fields.some((f) => f.required && !(values[f.name] ?? "").trim())
    );
  }, [config, sections, values]);

  const save = useMutation({
    mutationFn: async (): Promise<{ id: string | null; persisted: boolean }> => {
      if (!config || !user) throw new Error("Not ready");
      const title = config.titleFrom(values).trim() || config.title;

      // Non-persisted tools (e.g. Due Diligence, Conditions Precedent) have no
      // documents-table row and run through the workflow only.
      if (!isPersistedTool(config.tool)) {
        fireN8nWebhook(config.tool, {
          tool: config.tool,
          userId: user.id,
          matterId,
          clientId,
          title,
          input: values,
          submittedAt: new Date().toISOString(),
        });
        return { id: null, persisted: false };
      }

      // Insert as 'generating' so the UI shows a pending state until the
      // workflow (N8N or edge function) returns the draft text.
      const { data, error } = await supabase
        .from("documents")
        .insert({
          user_id: user.id,
          tool: config.tool,
          title,
          status: "generating",
          input: values,
          matter_id: matterId,
          client_id: clientId,
        })
        .select("id")
        .single();
      if (error) throw error;

      // Fire N8N in parallel with the edge function — both run, neither blocks.
      fireN8nWebhook(config.tool, {
        tool: config.tool,
        documentId: data.id,
        userId: user.id,
        matterId,
        clientId,
        title,
        input: values,
        submittedAt: new Date().toISOString(),
      });

      const { data: genData, error: genError } = await supabase.functions.invoke("generate-document", {
        body: { documentId: data.id },
      });
      if (genError) {
        console.warn("generate-document error:", genError);
        // Surface quota errors specifically so the user understands the cause.
        const detail = (genData as { error?: string; code?: string } | null) ?? null;
        if (detail?.code === "QUOTA_EXCEEDED" || /Monthly draft limit/i.test(detail?.error ?? "")) {
          toast.error("Monthly draft limit reached", {
            description:
              "The Solo plan includes 50 drafts per month. Upgrade to Professional for unlimited drafts under fair use.",
          });
        } else if (detail?.code === "FAIR_USE_EXCEEDED" || /Fair-use limit/i.test(detail?.error ?? "")) {
          toast.error("Fair-use limit reached", {
            description:
              "Professional includes unlimited drafts under a fair-use policy of 300 per month. Contact us about Enterprise volume pricing for consistently higher volumes.",
          });
        } else {
          toast.warning("Draft saved, but generation failed", {
            description: "You can retry generation from the document page.",
          });
        }
      }

      return { id: data.id, persisted: true };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["all-documents"] });
      if (matterId) queryClient.invalidateQueries({ queryKey: ["matter-documents", matterId] });
      if (clientId) queryClient.invalidateQueries({ queryKey: ["client-doc-count", clientId] });
      onClose();
      if (result.persisted && result.id) {
        toast.success("Sent for drafting", {
          description: "We'll notify you the moment your draft is ready.",
        });
        navigate(`/documents/${result.id}`);
      } else {
        toast.success("Submitted", {
          description: "Your request has been sent. Results will appear once they're ready.",
        });
      }
    },
    onError: (err: Error) => {
      toast.error("Could not submit", { description: err.message });
    },
  });

  if (!config) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-foreground/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-[640px] animate-fade-in flex-col overflow-hidden rounded-t-modal border border-hairline bg-background shadow-panel sm:rounded-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-hairline bg-card px-6 py-5">
          <div className="flex-1">
            <div className="eyebrow mb-1.5">{config.tool === "lod" ? "Letter of Demand" : config.tool}</div>
            <h2 className="font-serif text-[1.5rem] leading-tight text-foreground">{config.title}</h2>
            <p className="mt-1.5 text-[0.82rem] font-light leading-[1.55] text-muted-foreground">
              {config.blurb}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-md border border-hairline text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form body */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (requiredMissing || save.isPending) return;
            save.mutate();
          }}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6">
            {sections.map((section) => (
              <fieldset key={section.heading} className="space-y-3.5">
                <legend className="label-uppercase mb-3">{section.heading}</legend>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  {section.fields.map((f) => {
                    const fullWidth = f.type === "textarea";
                    return (
                      <div key={f.name} className={fullWidth ? "sm:col-span-2" : ""}>
                        <ToolFormField
                          field={f}
                          value={values[f.name] ?? ""}
                          onChange={(v) => setValues((prev) => ({ ...prev, [f.name]: v }))}
                        />
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            ))}
            {config.tool === "lod" && <MoraInterestPreview values={values} />}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-hairline bg-card px-6 py-4">
            <p className="text-[0.72rem] font-light leading-[1.5] text-muted-foreground">
              Saved to your account.{" "}
              <span className="text-ink-mid">
                You'll be notified the moment your draft is ready.
              </span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-btn border border-hairline px-4 py-2 text-[0.82rem] font-medium text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={requiredMissing || save.isPending}
                className={cn(
                  "rounded-btn bg-foreground px-5 py-2 text-[0.82rem] font-semibold text-background transition-all",
                  requiredMissing || save.isPending
                    ? "cursor-not-allowed opacity-50"
                    : "hover:-translate-y-px hover:bg-ink-mid"
                )}
              >
                {save.isPending ? "Sending for drafting…" : "Draft"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
